import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { groupsApi } from '../../services/groups-api-client';
import { mutationErrorMessage } from '../../lib/mutation-error';
import { claimDisplayName, holdDeleteConfirmCopy, type ClaimEditNote } from '../../lib/dinnerClaimEdit';
import { formatInZone, resolveTripTimeZone, wallClockToIso } from '../../lib/tripTime';
import AddDinnerGuest from './dinners/AddDinnerGuest';
import ClaimRow from './dinners/ClaimRow';
import EditClaimSheet from './dinners/EditClaimSheet';
import EditDinner from './dinners/EditDinner';
import type {
  CreateDinnerHoldRequest,
  GroupDetail,
  GroupDinnerClaim,
  GroupDinnerHold,
  GroupInvitee,
} from '../../types/groups';

const DINNER_HOLDS_KEY = (groupId: string) => ['group-dinner-holds', groupId];

/** Which seat's sheet is open. Held by the tab, not the card — see `hopToConflict`. */
interface OpenClaim {
  holdId: string;
  claimId: string;
}

function HoldCard({
  hold,
  holds,
  group,
  groupId,
  timeZone,
  timeZoneIsFallback,
  roster,
  refreshing,
  openClaimId,
  onOpenClaim,
  onCloseClaim,
  onOpenConflict,
  onOpenGuests,
  onRosterChanged,
}: {
  hold: GroupDinnerHold;
  /** Every non-cancelled hold, so a seat can be moved to another table. */
  holds: GroupDinnerHold[];
  /** The whole group, for the change-notice dialog an edit can open. */
  group: GroupDetail;
  groupId: string;
  timeZone: string;
  timeZoneIsFallback: boolean;
  roster: GroupInvitee[];
  /** A background refetch is in flight, so the numbers on screen may be behind. */
  refreshing: boolean;
  openClaimId: string | null;
  onOpenClaim: (claimId: string) => void;
  onCloseClaim: () => void;
  onOpenConflict: (holdId: string, from: GroupDinnerClaim) => void;
  onOpenGuests: () => void;
  onRosterChanged: () => void;
}) {
  const [showClaims, setShowClaims] = useState(false);
  const [claimNote, setClaimNote] = useState<ClaimEditNote | null>(null);
  const queryClient = useQueryClient();
  const invalidateHolds = () => queryClient.invalidateQueries(DINNER_HOLDS_KEY(groupId));
  const deleteMutation = useMutation(
    () => groupsApi.deleteDinnerHold(groupId, hold.id),
    { onSuccess: invalidateHolds },
  );
  const deleteError = mutationErrorMessage(deleteMutation.error);

  const full = hold.availableSpots <= 0;
  const openClaim = openClaimId
    ? hold.claims.find((claim) => claim.id === openClaimId) ?? null
    : null;

  /*
    A refetch can take the open seat out from under the sheet — the guest
    released it from their own phone, or another host moved them. Closing with a
    sentence beats rendering a sheet whose controls all act on a row that is not
    there any more.
  */
  useEffect(() => {
    if (openClaimId && !openClaim) {
      setClaimNote({
        severity: 'info',
        message: 'That seat is no longer on this table, so Studio closed it.',
        status: '',
      });
      onCloseClaim();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openClaimId, openClaim]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>{hold.venueName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatInZone(hold.reservedAt, hold.timeZone || timeZone)}{hold.address ? ` · ${hold.address}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Chip
            size="small"
            color={full ? 'default' : 'success'}
            label={`${hold.claimedSpots}/${hold.totalSpots} claimed`}
          />
          <EditDinner
            group={group}
            hold={hold}
            roster={roster}
            tripZone={timeZone}
            tripZoneIsFallback={timeZoneIsFallback}
            onNote={setClaimNote}
          />
          <IconButton
            size="small"
            aria-label={`Delete ${hold.venueName}`}
            disabled={deleteMutation.isLoading}
            onClick={() => {
              /*
                Names the people, not "any claims". Now that a host can take ONE
                guest off a table, an abstract "any claims are removed too" reads
                like the way to do it — and this button loses the whole guest
                list on the night. `holdDeleteConfirmCopy` says so in those words,
                and the per-claim confirm is a different widget entirely: a
                window.confirm renders with generic OK/Cancel on iOS, so wording
                alone could never have separated the two.
              */
              if (window.confirm(holdDeleteConfirmCopy(hold))) {
                deleteMutation.mutate();
              }
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      {deleteError ? <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert> : null}
      {/* Outcomes the sheet closed on land here, because the sheet is gone by then. */}
      {claimNote ? (
        <Alert severity={claimNote.severity} sx={{ mt: 1 }} onClose={() => setClaimNote(null)}>
          {claimNote.message}
        </Alert>
      ) : null}
      {hold.notes ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{hold.notes}</Typography>
      ) : null}
      {hold.claims.length > 0 ? (
        <>
          <Button size="small" sx={{ mt: 1 }} onClick={() => setShowClaims((prev) => !prev)}>
            {showClaims ? 'Hide guests' : `Who's coming (${hold.claims.length})`}
          </Button>
          <Collapse in={showClaims || openClaimId !== null} unmountOnExit>
            <Divider sx={{ my: 1 }} />
            {/*
              Dimmed while a refetch is in flight. There is deliberately no
              optimistic cache write, so for ~300ms after an edit the chip still
              shows the old number — dimming makes that read as loading rather
              than as frozen.

              Not while a sheet is open: the row under it already dims itself,
              and two nested 0.55s compound to 0.3, which reads as broken rather
              than as busy.
            */}
            <Stack spacing={0.5} sx={{ opacity: refreshing && openClaimId === null ? 0.55 : 1 }}>
              {hold.claims.map((claim) => (
                <ClaimRow
                  key={claim.id}
                  claim={claim}
                  displayName={claimDisplayName(claim, roster)}
                  busy={refreshing && openClaimId === claim.id}
                  onOpen={() => { setClaimNote(null); onOpenClaim(claim.id); }}
                />
              ))}
            </Stack>
          </Collapse>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          No claims yet.
        </Typography>
      )}
      {/*
        Rendered outside the Collapse on purpose. A `dinner_conflict` refusal can
        hop the host to this guest's seat on ANOTHER table, and that table's
        guest list is almost certainly collapsed — a sheet mounted inside an
        unmounted Collapse would simply never appear.
      */}
      {openClaim ? (
        <EditClaimSheet
          key={openClaim.id}
          open
          groupId={groupId}
          hold={hold}
          holds={holds}
          claim={openClaim}
          displayName={claimDisplayName(openClaim, roster)}
          timeZone={timeZone}
          onClose={onCloseClaim}
          onChanged={invalidateHolds}
          onNotice={setClaimNote}
          onOpenGuests={onOpenGuests}
          onOpenConflict={onOpenConflict}
        />
      ) : null}
      {/*
        The host's own way in. Guests claim from their trip page, but that needs
        a sign-in or a forwarded invite link — so a guest with neither an email
        nor a phone on file cannot reach any claim path that exists, and somebody
        has to seat them.
      */}
      <AddDinnerGuest
        groupId={groupId}
        hold={hold}
        roster={roster}
        onSeated={invalidateHolds}
        onRosterChanged={onRosterChanged}
      />
    </Paper>
  );
}

export default function DinnersTab({
  group,
  onRosterChanged,
  onOpenGuests,
}: {
  group: GroupDetail;
  /**
   * Seating an off-roster guest writes an invitee, so the group payload this tab
   * was handed is stale the moment it happens.
   */
  onRosterChanged: () => void;
  /**
   * A roster-linked seat takes its name from the Guests tab, so the refusal that
   * says so has to be able to open it. The page owns the tab state.
   */
  onOpenGuests: () => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching, error } = useQuery(
    DINNER_HOLDS_KEY(group.id),
    () => groupsApi.listDinnerHolds(group.id),
    {
      staleTime: 30_000,
      /*
        Availability that is 30 seconds stale is how this screen offers a seat
        the server then refuses. On a night with two hosts editing the same
        tables from two phones, that is every other tap.
      */
      refetchOnMount: 'always',
    },
  );
  const { timeZone, isFallback: zoneIsFallback } = resolveTripTimeZone(group.timeZone);
  // The host endpoint returns cancelled holds too (the guest one filters them).
  // Without this a deleted hold reappears with its claim count intact and the
  // host retries a delete that already worked.
  const holds = (data || []).filter((hold) => hold.status !== 'cancelled');

  const [openClaim, setOpenClaim] = useState<OpenClaim | null>(null);
  const [hopNote, setHopNote] = useState<string | null>(null);

  /*
    The payoff of host release existing at all. A move refused with
    `dinner_conflict` names the other table; this opens that guest's seat there,
    so the refusal becomes a two-tap fix instead of a dead end.

    Held by the tab rather than by HoldCard because the destination is a
    DIFFERENT card. The claim id is resolved from the list on screen — by invitee
    when there is one, by name for a `+1` that belongs to nobody the roster
    knows — and when it is not there, the honest answer is a sentence rather than
    an empty sheet.
  */
  const hopToConflict = (targetHoldId: string, from: GroupDinnerClaim) => {
    const target = holds.find((hold) => hold.id === targetHoldId);
    const match = target?.claims.find((claim) =>
      from.inviteeId ? claim.inviteeId === from.inviteeId : claim.guestName === from.guestName,
    );
    setHopNote(
      match
        ? null
        : `Studio could not find that guest's other seat on this screen — reload the tables.`,
    );
    setOpenClaim(match ? { holdId: targetHoldId, claimId: match.id } : null);
  };

  const [venueName, setVenueName] = useState('');
  const [address, setAddress] = useState('');
  const [reservedLocal, setReservedLocal] = useState('');
  const [totalSpots, setTotalSpots] = useState('4');
  const [notes, setNotes] = useState('');

  const createMutation = useMutation(
    (body: CreateDinnerHoldRequest) => groupsApi.createDinnerHold(group.id, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(DINNER_HOLDS_KEY(group.id));
        setVenueName(''); setAddress(''); setReservedLocal(''); setTotalSpots('4'); setNotes('');
      },
    },
  );

  const spots = Number.parseInt(totalSpots, 10);
  // Upper bound keeps us inside INT4 and rejects fat-finger values before the API.
  const canCreate = venueName.trim().length > 0 && Number.isFinite(spots) && spots > 0 && spots <= 100000;
  const createError = mutationErrorMessage(createMutation.error);

  const handleCreate = () => {
    if (!canCreate) return;
    createMutation.mutate({
      venueName: venueName.trim(),
      address: address.trim(),
      // Pinned to the trip's zone, not the browser's: a host in New York and a
      // host in Lisbon must store the same instant from the same keystrokes.
      reservedAt: wallClockToIso(reservedLocal, timeZone),
      timeZone,
      totalSpots: spots,
      notes: notes.trim(),
    });
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800}>Hold a dinner</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Hold a reservation with a set number of spots. Guests claim a spot from their own trip page —
          first come, first served, and it never oversells.
        </Typography>
        {createError ? <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert> : null}
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth label="Restaurant / venue" value={venueName}
              onChange={(e) => setVenueName(e.target.value.slice(0, 500))}
            />
            <TextField
              label="Total spots" type="number" value={totalSpots}
              onChange={(e) => setTotalSpots(e.target.value)}
              inputProps={{ min: 1 }} sx={{ maxWidth: { sm: 140 } }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth label="Address (optional)" value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            <TextField
              label={`When (${timeZone})`} type="datetime-local" value={reservedLocal}
              onChange={(e) => setReservedLocal(e.target.value)}
              helperText={zoneIsFallback ? 'This trip has no time zone set — using yours.' : 'Local time at the trip.'}
              InputLabelProps={{ shrink: true }} sx={{ maxWidth: { sm: 260 } }}
            />
          </Stack>
          <TextField
            fullWidth multiline minRows={2} label="Notes for guests (optional)" value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
        <Button
          variant="contained" sx={{ mt: 2 }}
          disabled={!canCreate || createMutation.isLoading}
          onClick={handleCreate}
        >
          {createMutation.isLoading ? 'Adding…' : 'Add dinner hold'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Dinner holds</Typography>
        {hopNote ? (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setHopNote(null)}>{hopNote}</Alert>
        ) : null}
        {isLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={20} /><Typography color="text.secondary">Loading…</Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">Couldn't load dinner holds.</Alert>
        ) : holds.length === 0 ? (
          <Typography color="text.secondary">No dinner holds yet.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {holds.map((hold) => (
              <HoldCard
                key={hold.id}
                hold={hold}
                holds={holds}
                group={group}
                groupId={group.id}
                timeZone={timeZone}
                timeZoneIsFallback={zoneIsFallback}
                roster={group.invitees}
                refreshing={isFetching}
                openClaimId={openClaim?.holdId === hold.id ? openClaim.claimId : null}
                onOpenClaim={(claimId) => { setHopNote(null); setOpenClaim({ holdId: hold.id, claimId }); }}
                onCloseClaim={() => setOpenClaim(null)}
                onOpenConflict={hopToConflict}
                onOpenGuests={onOpenGuests}
                onRosterChanged={onRosterChanged}
              />
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
